import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Rhythm Trainer v1 lexicon entries — 1 tool-level + 2 sub-mode + 5 meters +
 * 4 subdivisions + 2 feedback wrappers = 14 entries. Authored by Bowie in the
 * Rhythm Trainer v1 scope memo; locked verbatim — do not edit without Bowie
 * review.
 *
 * Outbound cross-link entries (`crosslink-rhythm-to-*`) live in
 * `lexicon-crosslink-entries.ts` to keep the cross-link semantics in one place.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §11
 * Bowie scope memo: .claude/agent-memory/bowie/project_rhythm_trainer_v1_scope.md
 */
export const RHYTHM_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'rhythm-trainer',
    category: 'rhythm',
    displayName: 'Rhythm Trainer',
    displayNameShort: 'Rhythm',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Train your ear for time signatures and rhythmic feel — the pulse beneath every song.',
      intermediate: 'Identify meters and subdivisions by ear — the rhythmic counterpart to chord and interval training.',
      advanced: 'Rhythmic perception is metric grouping plus subdivision feel — both trainable by ear.',
    },
    tagLabel: { beginner: 'rhythm', intermediate: 'meter + feel', advanced: 'metric perception' },
  },
  {
    id: 'rhythm-meter-id',
    category: 'rhythm',
    displayName: 'Meter ID',
    displayNameShort: 'Meter',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Listen to a beat and identify the time signature — how the notes are grouped.',
      intermediate: 'Identify the meter from the accent pattern: 4/4, 3/4, 6/8, 5/4, or 7/8.',
      advanced: 'Identify metric grouping — simple, compound, and asymmetric meters from accent + subdivision.',
    },
    tagLabel: { beginner: 'time signature', intermediate: 'meter', advanced: 'metric grouping' },
  },
  {
    id: 'rhythm-subdivision-id',
    category: 'rhythm',
    displayName: 'Subdivision ID',
    displayNameShort: 'Feel',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Hear a beat and identify how each beat is divided — into 2s, 4s, 3s, or swung.',
      intermediate: 'Identify the subdivision: straight 8ths, 16ths, triplets, or swing.',
      advanced: 'Identify subdivision and feel — straight vs swung articulation against the underlying pulse.',
    },
    tagLabel: { beginner: 'feel', intermediate: 'subdivision', advanced: 'subdivision + articulation' },
  },
  {
    id: 'rhythm-meter-4-4',
    category: 'rhythm',
    displayName: '4/4',
    displayNameShort: '4/4',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Four beats per measure — the most common time in pop, rock, and hip-hop.',
      intermediate: '4/4 — four quarter-note beats; the default meter in most popular music.',
      advanced: 'Simple quadruple. Strong-weak-medium-weak accent pattern; the "common time" reference.',
    },
    tagLabel: { beginner: 'four-feel', intermediate: '4/4', advanced: 'simple quadruple' },
  },
  {
    id: 'rhythm-meter-3-4',
    category: 'rhythm',
    displayName: '3/4',
    displayNameShort: '3/4',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Three beats per measure — the waltz feel.',
      intermediate: '3/4 — three quarter-note beats; waltz time. Strong-weak-weak accent.',
      advanced: 'Simple triple. ONE-two-three groupings; distinguished from 6/8 by single-beat divisions, not duple-of-triplet.',
    },
    tagLabel: { beginner: 'waltz', intermediate: '3/4', advanced: 'simple triple' },
  },
  {
    id: 'rhythm-meter-6-8',
    category: 'rhythm',
    displayName: '6/8',
    displayNameShort: '6/8',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Six notes per measure but felt as two — like a fast jig or a slow rock ballad.',
      intermediate: '6/8 — two beats of three. Often mistaken for 3/4, but the grouping is duple, not triple.',
      advanced: 'Compound duple. Two dotted-quarter beats each subdividing into three 8ths; primary distinction from 3/4 is the duple top-level grouping.',
    },
    tagLabel: { beginner: 'jig feel', intermediate: '6/8', advanced: 'compound duple' },
  },
  {
    id: 'rhythm-meter-5-4',
    category: 'rhythm',
    displayName: '5/4',
    displayNameShort: '5/4',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Five beats per measure — the "Take Five" feel. Sounds off-kilter at first.',
      intermediate: '5/4 — asymmetric meter, typically grouped 3+2 (Take Five) or 2+3.',
      advanced: 'Asymmetric meter. 3+2 grouping is canonical (Brubeck); listen for two accent pulses per measure with uneven spacing.',
    },
    tagLabel: { beginner: 'Take Five', intermediate: '5/4', advanced: 'asymmetric 3+2' },
  },
  {
    id: 'rhythm-meter-7-8',
    category: 'rhythm',
    displayName: '7/8',
    displayNameShort: '7/8',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Seven beats per measure — "Money" by Pink Floyd uses this. Feels like 2+2+3.',
      intermediate: '7/8 — asymmetric meter, typically grouped 2+2+3. Three accent pulses per measure with the last group longer.',
      advanced: 'Asymmetric. 2+2+3 is the most common Western grouping; alternate 3+2+2 or 2+3+2 produce different feels but share the seven-eighth-note total.',
    },
    tagLabel: { beginner: 'Money feel', intermediate: '7/8', advanced: 'asymmetric 2+2+3' },
  },
  {
    id: 'rhythm-sub-8ths',
    category: 'rhythm',
    displayName: 'Straight 8ths',
    displayNameShort: '8ths',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Each beat split evenly in two — the steady "1-and-2-and" rock pulse.',
      intermediate: 'Straight 8ths — two equal subdivisions per beat. The default rock/pop subdivision.',
      advanced: 'Duple subdivision, equal articulation; the unmarked reference against which swing and triplets are perceived.',
    },
    tagLabel: { beginner: '1-and-2-and', intermediate: 'straight 8ths', advanced: 'duple subdivision' },
  },
  {
    id: 'rhythm-sub-16ths',
    category: 'rhythm',
    displayName: 'Straight 16ths',
    displayNameShort: '16ths',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Each beat split into four — the fast funk / disco pulse.',
      intermediate: 'Straight 16ths — four equal subdivisions per beat. Common in funk, disco, and Latin music.',
      advanced: 'Quadruple subdivision; perception relies on hearing twice the density of straight 8ths.',
    },
    tagLabel: { beginner: '1-e-and-a', intermediate: 'straight 16ths', advanced: 'quadruple subdivision' },
  },
  {
    id: 'rhythm-sub-triplets',
    category: 'rhythm',
    displayName: 'Triplets',
    displayNameShort: 'Triplets',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Each beat split into three — the "1-trip-let" classical or shuffle feel.',
      intermediate: 'Triplet 8ths — three equal subdivisions per beat. Compound feel inside a simple meter.',
      advanced: "Triple subdivision; three equal articulations per beat. Distinguished from swing by even spacing (1:1:1) versus swing's long-short (2:1).",
    },
    tagLabel: { beginner: '1-trip-let', intermediate: 'triplets', advanced: 'triple subdivision' },
  },
  {
    id: 'rhythm-sub-swing',
    category: 'rhythm',
    displayName: 'Swing 8ths',
    displayNameShort: 'Swing',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A long-short bounce — the swinging jazz and shuffle blues feel.',
      intermediate: 'Swing 8ths — the second 8th of each beat is delayed, creating a long-short (2:1) bounce.',
      advanced: 'Swing articulation — the off-beat 8th is delayed to roughly the third triplet position. Hard swing is 2:1; softer swing approaches 1:1 at fast tempos.',
    },
    tagLabel: { beginner: 'bounce', intermediate: 'swing', advanced: 'long-short articulation' },
  },
  {
    id: 'rhythm-meter-feedback',
    category: 'rhythm',
    displayName: 'Meter feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Listen for the accent — beat 1 is loudest. Count the beats between accents to find the meter.',
      intermediate: "The accent marks beat 1. Count the beats between accents; that's your top-number. Compound meters (6/8) group beats into pairs of three.",
      advanced: 'Metric perception = accent placement + subdivision grouping. Simple meters subdivide in 2; compound in 3; asymmetric in uneven groups (3+2, 2+2+3).',
    },
    tagLabel: { beginner: 'count the accents', intermediate: 'accent + grouping', advanced: 'metric grouping' },
  },
  {
    id: 'rhythm-subdivision-feedback',
    category: 'rhythm',
    displayName: 'Subdivision feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The beat stays the same; what changed is how each beat is filled in. Listen for the speed and evenness of the fills.',
      intermediate: 'Beats are fixed; subdivision varies. 8ths = 2 per beat; 16ths = 4; triplets = 3 even; swing = 2 uneven (long-short).',
      advanced: 'Subdivision perception is about ratio and evenness. Triple vs. duple subdivision is the cleanest contrast; swing-vs-triplet is the subtlest (both triple-rooted, but articulated differently).',
    },
    tagLabel: { beginner: 'how beats are filled', intermediate: 'fill ratio', advanced: 'subdivision ratio' },
  },
  // ───── v1.5 Grouping ID (placeholder copy — Bowie §8 rewrites; IDs + structure locked) ─────
  {
    id: 'rhythm-grouping',
    category: 'rhythm',
    displayName: 'Grouping ID',
    displayNameShort: 'Grouping',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You’re told the meter — now pick how its beats clump together into longer and shorter groups.',
      intermediate: 'Identify the internal grouping of an irregular meter — where the longer beat falls.',
      advanced: 'Identify the additive grouping of an asymmetric meter from its accent placement.',
    },
    tagLabel: { beginner: 'group the beats', intermediate: 'grouping ID', advanced: 'additive grouping' },
  },
  {
    id: 'rhythm-grouping-5-4-3-2',
    category: 'rhythm',
    displayName: '3+2',
    displayNameShort: '3+2',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Five beats split 3 then 2 — accents on beats 1 and 4.',
      intermediate: '5/4 grouped 3+2: a three-beat cell then a two-beat cell. Accents on 1 and 4.',
      advanced: '5/4 additive 3+2 — secondary accent at beat 4 (the second cell start).',
    },
    tagLabel: { beginner: '3 then 2', intermediate: '3+2', advanced: '3+2' },
  },
  {
    id: 'rhythm-grouping-5-4-2-3',
    category: 'rhythm',
    displayName: '2+3',
    displayNameShort: '2+3',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Five beats split 2 then 3 — accents on beats 1 and 3.',
      intermediate: '5/4 grouped 2+3: a two-beat cell then a three-beat cell. Accents on 1 and 3.',
      advanced: '5/4 additive 2+3 — secondary accent at beat 3 (the second cell start).',
    },
    tagLabel: { beginner: '2 then 3', intermediate: '2+3', advanced: '2+3' },
  },
  {
    id: 'rhythm-grouping-7-8-2-2-3',
    category: 'rhythm',
    displayName: '2+2+3',
    displayNameShort: '2+2+3',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Two fast beats, then a long push — that 2+2+3 lean comes at the end.',
      intermediate: '7/8 grouped 2+2+3: two short cells then a long one. Accents on 1, 3, 5.',
      advanced: '7/8 additive 2+2+3 — secondary accents at the 3rd and 5th eighths.',
    },
    tagLabel: { beginner: 'long last', intermediate: '2+2+3', advanced: '2+2+3' },
  },
  {
    id: 'rhythm-grouping-7-8-3-2-2',
    category: 'rhythm',
    displayName: '3+2+2',
    displayNameShort: '3+2+2',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A long push up front, then two fast beats — that 3+2+2 lean comes first.',
      intermediate: '7/8 grouped 3+2+2: a long cell then two short ones. Accents on 1, 4, 6.',
      advanced: '7/8 additive 3+2+2 — secondary accents at the 4th and 6th eighths.',
    },
    tagLabel: { beginner: 'long first', intermediate: '3+2+2', advanced: '3+2+2' },
  },
  {
    id: 'rhythm-grouping-7-8-2-3-2',
    category: 'rhythm',
    displayName: '2+3+2',
    displayNameShort: '2+3+2',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Fast, long push, fast — the 2+3+2 lean sits right in the middle.',
      intermediate: '7/8 grouped 2+3+2: short, long, short. Accents on 1, 3, 6.',
      advanced: '7/8 additive 2+3+2 — secondary accents at the 3rd and 6th eighths.',
    },
    tagLabel: { beginner: 'long middle', intermediate: '2+3+2', advanced: '2+3+2' },
  },
]
